import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { Button, Card, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime } from '../i18n.js';
import { ME_KEY, useMe } from '../session.js';

/** US-11: "Daten exportieren" and "Konto löschen" on the account page. */
export function PrivacyCards() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const client = useQueryClient();
  const navigate = useNavigate();
  const timeZone = me.data?.account.timezone;

  const exportData = useMutation({
    mutationFn: () => unwrap(api.POST('/account/export')),
  });

  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE('/account', { body: { password } })),
    onSuccess: (data) => {
      client.setQueryData(ME_KEY, null);
      client.clear();
      void navigate(`/account-deleted?until=${encodeURIComponent(data.deleteAt)}`, {
        replace: true,
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    remove.mutate();
  };
  const removeError =
    remove.error instanceof ApiError && remove.error.code === 'CONFLICT'
      ? t('privacy.delete.subscription')
      : remove.error instanceof ApiError && remove.error.code === 'INVALID_CREDENTIALS'
        ? t('privacy.delete.wrongPassword')
        : remove.error
          ? errorMessage(remove.error, t)
          : undefined;

  return (
    <>
      <Card>
        <h2 className="mb-2 text-xl font-semibold">{t('privacy.export.title')}</h2>
        <p className="mb-4 text-slate-800">{t('privacy.export.text')}</p>
        {exportData.isSuccess ? (
          <Notice kind="success">
            {t('privacy.export.sent', {
              date: formatDateTime(exportData.data.expiresAt, i18n.language, timeZone),
            })}
          </Notice>
        ) : null}
        {exportData.isError ? (
          <Notice kind="error">
            {exportData.error instanceof ApiError && exportData.error.code === 'RATE_LIMITED'
              ? t('privacy.export.once')
              : errorMessage(exportData.error, t)}
          </Notice>
        ) : null}
        <Button
          variant="secondary"
          className="mt-2"
          busy={exportData.isPending}
          disabled={exportData.isSuccess}
          onClick={() => exportData.mutate()}
        >
          {t('privacy.export.button')}
        </Button>
      </Card>
      <Card>
        <h2 className="mb-2 text-xl font-semibold">{t('privacy.delete.title')}</h2>
        <p className="mb-2 text-slate-800">{t('privacy.delete.text')}</p>
        <p className="mb-4 text-slate-800">{t('privacy.delete.undo')}</p>
        {confirming ? (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <TextField
              label={t('privacy.delete.password')}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={removeError}
              required
            />
            <div className="flex flex-wrap gap-2">
              <Button type="submit" variant="danger" busy={remove.isPending}>
                {t('privacy.delete.confirm')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirming(false);
                  setPassword('');
                  remove.reset();
                }}
              >
                {t('app.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            {t('privacy.delete.button')}
          </Button>
        )}
      </Card>
    </>
  );
}

/** After the deletion (signed out): what happens next. */
export function AccountDeleted() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const until = params.get('until');
  return (
    <Card>
      <h1 className="mb-4 text-2xl font-bold">{t('privacy.deleted.title')}</h1>
      <p>
        {until && !Number.isNaN(Date.parse(until))
          ? t('privacy.deleted.text', { date: formatDate(until, i18n.language) })
          : t('privacy.deleted.textNoDate')}
      </p>
    </Card>
  );
}

/** /restore-account?token=… from the deletion e-mail. */
export function RestoreAccount() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const restore = useMutation({
    mutationFn: () => unwrap(api.POST('/account/restore', { body: { token } })),
  });
  return (
    <Card>
      <h1 className="mb-4 text-2xl font-bold">{t('privacy.restore.title')}</h1>
      {!token ? (
        <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
      ) : restore.isSuccess ? (
        <>
          <Notice kind="success">{t('privacy.restore.done')}</Notice>
          <p className="mt-4">
            <Link to="/login" className="font-semibold text-blue-800 underline">
              {t('auth.reset.toLogin')}
            </Link>
          </p>
        </>
      ) : (
        <>
          <p className="mb-4">{t('privacy.restore.text')}</p>
          {restore.isError ? <Notice kind="error">{errorMessage(restore.error, t)}</Notice> : null}
          <Button busy={restore.isPending} onClick={() => restore.mutate()}>
            {t('privacy.restore.button')}
          </Button>
        </>
      )}
    </Card>
  );
}

/** /export?token=… from the export e-mail (signed in): downloads the JSON file. */
export function ExportDownload() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const url = useRef<string | null>(null);
  const download = useMutation({
    mutationFn: async () => {
      const data = await unwrap(api.POST('/account/export/download', { body: { token } }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url.current;
      a.download = `website-guard-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    },
  });
  useEffect(
    () => () => {
      if (url.current) URL.revokeObjectURL(url.current);
    },
    [],
  );
  return (
    <>
      <PageTitle>{t('privacy.download.title')}</PageTitle>
      <Card className="max-w-2xl">
        {!token ? (
          <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
        ) : (
          <>
            <p className="mb-4">{t('privacy.download.text')}</p>
            {download.isSuccess ? (
              <Notice kind="success">{t('privacy.download.done')}</Notice>
            ) : null}
            {download.isError ? (
              <Notice kind="error">{errorMessage(download.error, t)}</Notice>
            ) : null}
            <Button busy={download.isPending} onClick={() => download.mutate()}>
              {t('privacy.download.button')}
            </Button>
          </>
        )}
      </Card>
    </>
  );
}
